/* eslint-disable better-mutation/no-mutation */
import initData from 'tradera/legacy/static/packages/init-data';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getSyiSlice } from 'tradera/apps/syi/state/selectors/syi';
import type { AppDispatch, GetState } from 'tradera/state/configure-store';

const defaultRegionValue = 'EU';

interface BuyerRegion {
    value: string;
    text: string;
}

interface BuyerRegionState {
    defaultBuyerRegion: BuyerRegion;
    availableAllowedBuyerRegions: BuyerRegion[];
    selectedBuyerRegion: string;
    selectedBuyerRegionText: string;
    availableAllowedBuyerRegionsForEditWithBids: boolean;
    isSetAllowedBuyerRegionToAtLeastEuEnabled: boolean;
}

const initModuleGlobals = (): BuyerRegionState | null => {
    if (!Array.isArray(initData.pageSpecificData?.allowedBuyerRegions)) {
        return null;
    }

    const availableAllowedBuyerRegions: BuyerRegion[] =
        initData.pageSpecificData.allowedBuyerRegions;
    const defaultBuyerRegion = availableAllowedBuyerRegions.find(
        (r: BuyerRegion) => r.value === defaultRegionValue
    ) as BuyerRegion;

    return {
        defaultBuyerRegion,
        availableAllowedBuyerRegions,
        selectedBuyerRegion: defaultBuyerRegion.value,
        selectedBuyerRegionText: defaultBuyerRegion.text,
        availableAllowedBuyerRegionsForEditWithBids: false,
        isSetAllowedBuyerRegionToAtLeastEuEnabled:
            initData?.featureSwitches?.[
                'international-shipping-schenker-eu-default'
            ] || false
    };
};

const buyerRegionSlice = createSlice({
    name: 'buyer-region',
    initialState: initModuleGlobals(),
    reducers: {
        setAllowedBuyerRegion: (
            state,
            action: PayloadAction<{ allowedBuyerRegion: string }>
        ) => {
            if (state) {
                const region = state.availableAllowedBuyerRegions.find(
                    (r) => r.value === action.payload.allowedBuyerRegion
                );
                const { value, text } = region || state.defaultBuyerRegion;

                state.selectedBuyerRegion = value;
                state.selectedBuyerRegionText = text;
            }
        },
        setAvailableAllowedBuyerRegions: (
            state,
            action: PayloadAction<{ availableRegions: BuyerRegion[] }>
        ) => {
            if (state) {
                state.availableAllowedBuyerRegions =
                    action.payload.availableRegions;
                state.availableAllowedBuyerRegionsForEditWithBids = true;
            }
        }
    }
});

export const setAvailableAllowedBuyerRegions =
    () => (dispatch: AppDispatch, getState: GetState) => {
        const draftState = getSyiSlice(getState()).draft;
        const regionState = getSyiSlice(getState()).buyerRegion;
        if (regionState) {
            const regions = regionState.availableAllowedBuyerRegions;
            const selectedRegion = regionState.selectedBuyerRegion;
            const filters: { [key: string]: (region: BuyerRegion) => boolean } =
                {
                    Sweden: (region) =>
                        ['Sweden', 'Nordic', 'EU', 'International'].indexOf(
                            region.value
                        ) > -1,
                    Nordic: (region) =>
                        ['Nordic', 'International'].indexOf(region.value) > -1,
                    EU: (region) =>
                        ['EU', 'International'].indexOf(region.value) > -1,
                    International: (region) =>
                        ['International'].indexOf(region.value) > -1
                };
            const useFilter = draftState.draftReason === 'ChangeItemWithBids';
            const filter =
                (useFilter && filters[selectedRegion]) || (() => true);
            const availableRegions = regions.filter(filter);
            dispatch(
                buyerRegionSlice.actions.setAvailableAllowedBuyerRegions({
                    availableRegions
                })
            );
        }
    };

export const { setAllowedBuyerRegion } = buyerRegionSlice.actions;

export default buyerRegionSlice.reducer;
